import { ApplicationFeeStatus, AppliedDiscountInfo, Money } from "shared/types";
import { formatToCurrency } from "shared/utils";
import { formatTitleFirstWord } from "utils/formatTitle";
import { cx } from "utils/classnames";
import { Check, Minus, FlipBackward } from "untitledui-js-base";
import { Cell } from "../Cell";
import { ApplicantOrInvitee } from "../../types";
import { Applicant } from "types/application";

type ExtendedDiscountInfo = AppliedDiscountInfo & {
  final_amount?: Money;
};

type ApplicantWithDiscount = Applicant & {
  discount_info?: ExtendedDiscountInfo;
};

interface ApplicationFeeCellProps {
  applicant: ApplicantOrInvitee;
}

const iconMap = {
  [ApplicationFeeStatus.Paid]: (
    <Check size="20" className="stroke-success-600" />
  ),
  [ApplicationFeeStatus.Refunded]: (
    <FlipBackward size="20" className="stroke-warning-500" />
  ),
  [ApplicationFeeStatus.Unpaid]: <Minus className="stroke-content-200" />
};

export const ApplicationFeeCell = ({
  applicant: applicantOrInvitee
}: ApplicationFeeCellProps) => {
  const isInvitee = "is_public" in applicantOrInvitee;

  if (
    isInvitee ||
    applicantOrInvitee.application_fee_status ===
      ApplicationFeeStatus.NoApplicationFee
  )
    return (
      <Cell isFirst isDataCell>
        -
      </Cell>
    );

  const applicantWithDiscount = applicantOrInvitee as ApplicantWithDiscount;

  if (applicantWithDiscount.application_fee_status === null)
    return (
      <Cell isFirst isDataCell>
        <span className="text-content-200">N/A</span>
      </Cell>
    );

  const applicationStatusFee = applicantWithDiscount.application_fee_status,
    paidApplicationFee = applicantWithDiscount.paid_application_fee ?? null,
    discountInfo = applicantWithDiscount.discount_info ?? null,
    applicationFee = applicantWithDiscount.application_fee ?? null,
    templateFeesTotal = applicantWithDiscount.template_fees_total;

  const withTemplateFees = (base: Money | null): Money | null => {
    if (!base || !templateFeesTotal?.cents) return base;
    return {
      cents: base.cents + templateFeesTotal.cents,
      currency: base.currency
    };
  };

  return (
    <Cell isFirst isDataCell>
      <div className="flex gap-2 items-center">
        {iconMap[applicationStatusFee]}
        <p
          className={cx(
            "font-medium",
            applicantWithDiscount.application_fee_status ===
              ApplicationFeeStatus.Unpaid
              ? "text-content-200"
              : "text-content-1000"
          )}
        >
          {formatTitleFirstWord(applicantWithDiscount.application_fee_status)}
        </p>
        <p
          className={cx(
            applicantWithDiscount.application_fee_status ===
              ApplicationFeeStatus.Unpaid
              ? "text-content-200"
              : "text-content-300"
          )}
        >
          {discountInfo?.discount_applied ? (
            <>
              (
              <span className="line-through">
                {formatToCurrency(
                  applicationStatusFee === ApplicationFeeStatus.Paid &&
                    paidApplicationFee &&
                    discountInfo.discount_amount
                    ? {
                        cents:
                          paidApplicationFee.cents +
                          (typeof discountInfo.discount_amount === "number"
                            ? discountInfo.discount_amount
                            : discountInfo.discount_amount.cents),
                        currency: paidApplicationFee.currency
                      }
                    : withTemplateFees(applicationFee),
                  { minimumFractionDigits: 2 }
                )}
              </span>{" "}
              {formatToCurrency(
                applicationStatusFee === ApplicationFeeStatus.Paid
                  ? paidApplicationFee
                  : withTemplateFees(discountInfo.final_amount ?? null),
                { minimumFractionDigits: 2 }
              )}
              )
            </>
          ) : (
            <>
              (
              {formatToCurrency(
                applicationStatusFee === ApplicationFeeStatus.Unpaid
                  ? withTemplateFees(applicationFee)
                  : paidApplicationFee
              )}
              )
            </>
          )}
        </p>
      </div>
    </Cell>
  );
};
